import {
  HotelCriteriaEnums,
  TopSportsEnums,
  TopFamilyEnums,
  TopWellnessEnums,
  HotelCriteriaIds,
  TopSportIds,
  TopFamilyIds,
  TopWellnessIds,
} from "@/lib/Types/searchForm";

/**
 * Gets the category filter name/action for a given category ID
 * @param categoryId The category ID
 * @returns The category filter name or action string, or false if not found
 */
export const getCategoryMapping = (categoryId: number): string | false => {
  // Top Family mappings
  if ([19, 20, 21, 22, 23, 77].includes(categoryId)) {
    const familyMappings: Record<TopFamilyIds, string> = {
      19: TopFamilyEnums.familyFriendly,
      20: TopFamilyEnums.childCare,
      21: TopFamilyEnums.animationsProgram,
      22: TopFamilyEnums.childrenPool,
      23: TopFamilyEnums.playground,
      77: TopFamilyEnums.waterSlides,
    };
    return `familie-${familyMappings[categoryId as TopFamilyIds]}`;
  }

  // Top Wellness mappings
  if ([39, 40].includes(categoryId)) {
    const wellnessMappings: Record<TopWellnessIds, string> = {
      39: TopWellnessEnums.massage,
      40: TopWellnessEnums.sauna,
    };
    return `wellness-${wellnessMappings[categoryId as TopWellnessIds]}`;
  }

  // Top Sport mappings
  if ([2, 3, 4, 5, 7, 9, 12, 16, 17, 34].includes(categoryId)) {
    const sportMappings: Partial<Record<TopSportIds, string>> = {
      4: TopSportsEnums.mountainBicycle,
      5: TopSportsEnums.golf,
      7: TopSportsEnums.fitness,
      9: TopSportsEnums.tennisSquash,
      12: TopSportsEnums.waterSport,
      16: TopSportsEnums.surfing,
      17: TopSportsEnums.diving,
      34: TopSportsEnums.indoorSwimmingPool,
    };
    return `sportangebot-${sportMappings[categoryId as TopSportIds]}`;
  }

  // Hotel Criteria mappings
  if ([30, 32, 33, 44, 46, 79, 80, 81, 82, 83].includes(categoryId)) {
    const hotelCriteriaMappings: Partial<Record<HotelCriteriaIds, string>> = {
      30: HotelCriteriaEnums.beachLocation,
      32: HotelCriteriaEnums.sandyBeach,
      33: HotelCriteriaEnums.pool,
      44: HotelCriteriaEnums.onlyForAdults,
      46: HotelCriteriaEnums.wlan,
      79: HotelCriteriaEnums.petAllowed,
      80: HotelCriteriaEnums.privatePool,
      81: HotelCriteriaEnums.swimupPool,
      82: HotelCriteriaEnums.atLeastTwoSeparateBedrooms,
      83: HotelCriteriaEnums.separateBedroom,
    };
    return `hotelkriterien-${
      hotelCriteriaMappings[categoryId as HotelCriteriaIds]
    }`;
  }

  return false;
};

/**
 * Determines if a filter change is a deselection
 * @param key The filter key
 * @param value The current value
 * @param prevValue The previous value
 * @returns True if the change is a deselection
 */
export const isDeselection = (
  key: string,
  value: string | null,
  prevValue: string | null
): boolean => {
  if (value === null || value === undefined) return true;

  switch (key) {
    case "stars":
      return value === "0" || value === "";
    case "minRecommendation":
      return value === "0" || value === "";
    case "price":
      return value === "6000" || value === "";
    case "flexibleOffers":
    case "specialDeals":
    case "bookOnOption":
    case "oceanView":
      return value !== "true" && prevValue === "true";
    default:
      return false;
  }
};

/**
 * Processes category changes and calls the provided tracking function for each change
 * @param currentParams Current filter parameters
 * @param prevParams Previous filter parameters
 * @param trackFn Function to call for each category change
 */
export const processCategoryChanges = (
  currentParams: Record<string, string | undefined>,
  prevParams: Record<string, string | undefined>,
  trackFn: (categoryId: string, isDeselected: boolean) => void
): void => {
  // Get current and previous categories
  const currentCategories = new Set(
    currentParams.categories?.split(",").filter(Boolean) || []
  );
  const prevCategories = new Set(
    prevParams.categories?.split(",").filter(Boolean) || []
  );

  // Track newly selected categories
  currentCategories.forEach((categoryId) => {
    if (!prevCategories.has(categoryId)) {
      trackFn(categoryId, false);
    }
  });

  // Track deselected categories
  prevCategories.forEach((categoryId) => {
    if (!currentCategories.has(categoryId)) {
      trackFn(categoryId, true);
    }
  });
};

/**
 * Processes room type changes and calls the provided tracking function for each change
 * @param currentParams Current filter parameters
 * @param prevParams Previous filter parameters
 * @param trackFn Function to call for each room type change
 */
export const processRoomTypeChanges = (
  currentParams: Record<string, string | undefined>,
  prevParams: Record<string, string | undefined>,
  trackFn: (roomTypeId: string, isDeselected: boolean) => void
): void => {
  const currentRoomTypes = new Set(
    currentParams.roomType?.split(",").filter(Boolean) || []
  );
  const prevRoomTypes = new Set(
    prevParams.roomType?.split(",").filter(Boolean) || []
  );

  // Track newly selected room types
  currentRoomTypes.forEach((roomTypeId) => {
    if (!prevRoomTypes.has(roomTypeId)) {
      trackFn(roomTypeId, false);
    }
  });

  // Track deselected room types
  prevRoomTypes.forEach((roomTypeId) => {
    if (!currentRoomTypes.has(roomTypeId)) {
      trackFn(roomTypeId, true);
    }
  });
};

/**
 * Processes tour operator changes and calls the provided tracking function for each change
 * @param currentParams Current filter parameters
 * @param prevParams Previous filter parameters
 * @param trackFn Function to call for each tour operator change
 */
/**
 * Processes airline changes and calls the provided tracking function for each change
 */
export const processAirlineChanges = (
  currentParams: Record<string, string | undefined>,
  prevParams: Record<string, string | undefined>,
  trackFn: (airlineCode: string, isDeselected: boolean) => void
): void => {
  const currentAirlines = new Set(
    currentParams.airline?.split(",").filter(Boolean) || []
  );
  const prevAirlines = new Set(
    prevParams.airline?.split(",").filter(Boolean) || []
  );

  currentAirlines.forEach((code) => {
    if (!prevAirlines.has(code)) {
      trackFn(code, false);
    }
  });

  prevAirlines.forEach((code) => {
    if (!currentAirlines.has(code)) {
      trackFn(code, true);
    }
  });
};

export const processTourOperatorChanges = (
  currentParams: Record<string, string | undefined>,
  prevParams: Record<string, string | undefined>,
  trackFn: (operatorId: string, isDeselected: boolean) => void
): void => {
  const currentOperators = new Set(
    currentParams.tourOperator?.split(",").filter(Boolean) || []
  );
  const prevOperators = new Set(
    prevParams.tourOperator?.split(",").filter(Boolean) || []
  );

  // Track newly selected operators
  currentOperators.forEach((operatorId) => {
    if (!prevOperators.has(operatorId)) {
      trackFn(operatorId, false);
    }
  });

  // Track deselected operators
  prevOperators.forEach((operatorId) => {
    if (!currentOperators.has(operatorId)) {
      trackFn(operatorId, true);
    }
  });
};
